import { Heart, Repeat2, MessageCircle } from 'lucide-react'
import { formatCount } from '@/lib/formatCount'
import { strings } from '@/strings'

export interface PostCountsRowProps {
    /** Reply count. Absent or not a safe count = no reply item. */
    replyCount?: number
    /** Repost (boost on ActivityPub) count. */
    repostCount?: number
    /** Like count. */
    likeCount?: number
    /** Labels reposts as boosts, as the feed's PostEngagementBar does. */
    isActivityPub?: boolean
    className?: string
}

/** A producer may omit a count or send junk; only a finite non-negative number is shown. */
function isCount(n: unknown): n is number {
    return typeof n === 'number' && Number.isFinite(n) && n >= 0
}

function CountItem({
    icon,
    count,
    label,
}: {
    icon: React.ReactNode
    count: number
    label: string
}) {
    // role="img" gives the item one accessible name ("12 likes") instead of a
    // bare number next to an unlabeled icon.
    return (
        <span
            role="img"
            aria-label={`${count.toLocaleString('en-US')} ${label}`}
            className="flex items-center gap-1.5"
        >
            {icon}
            {/* Like the feed's PostEngagementBar: the icon stays for 0, the digit does not. */}
            {count > 0 && <span className="text-xs">{formatCount(count)}</span>}
        </span>
    )
}

/**
 * Read-only like / repost / reply counts for a compact post card.
 *
 * Deliberately not PostEngagementBar: no buttons, no handlers, no
 * stopPropagation, so a click on the row does exactly what a click on the
 * surrounding card does. Renders nothing at all (no empty wrapper, so no
 * spacing) when none of the counts is present.
 */
export function PostCountsRow({
    replyCount,
    repostCount,
    likeCount,
    isActivityPub = false,
    className,
}: PostCountsRowProps) {
    const items: React.ReactNode[] = []
    const iconClass = 'w-4 h-4'

    // Same order as the feed: reply, repost, like.
    if (isCount(replyCount)) {
        items.push(
            <CountItem
                key="reply"
                icon={<MessageCircle className={iconClass} aria-hidden="true" />}
                count={replyCount}
                label={strings.posts.repliesLabel(replyCount)}
            />
        )
    }
    if (isCount(repostCount)) {
        items.push(
            <CountItem
                key="repost"
                icon={<Repeat2 className={iconClass} aria-hidden="true" />}
                count={repostCount}
                label={
                    isActivityPub
                        ? strings.posts.boostsLabel(repostCount)
                        : strings.posts.repostsLabel(repostCount)
                }
            />
        )
    }
    if (isCount(likeCount)) {
        items.push(
            <CountItem
                key="like"
                icon={<Heart className={iconClass} aria-hidden="true" />}
                count={likeCount}
                label={strings.posts.likesLabel(likeCount)}
            />
        )
    }

    if (items.length === 0) return null

    return (
        <div
            role="group"
            aria-label={strings.posts.engagementCounts}
            data-testid="post-counts-row"
            className={`flex items-center gap-6 text-text-secondary-light dark:text-text-secondary-dark${className ? ` ${className}` : ''}`}
        >
            {items}
        </div>
    )
}
